/**
 * Central API client for JSON:API. Reads auth token from Redux store.
 * Used by TanStack Query hooks for server state.
 */
import { store } from "@/redux/store";
import { API_BASE_URL, API_ORIGIN } from "@/config/api";
import {
  mapGrievanceCollection,
  mapGrievanceInterface,
  mapGrievanceUpdateCollection,
} from "@/utils/case-utils";
import type { Grievance, GrievanceUpdate, Image } from "@/data/grievances";
import { addLog } from "@/logger";

function getToken(): string | null {
  return store.getState().auth.token;
}

async function fetchApi<T>(
  path: string,
  options: RequestInit & { baseUrl?: string } = {}
): Promise<T> {
  const { baseUrl = API_BASE_URL, ...init } = options;
  const url = path.startsWith("http") ? path : `${baseUrl.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
  const token = getToken();
  const method = (init.method || "GET").toUpperCase();
  const headers: Record<string, string> = {
    Accept: "application/vnd.api+json",
    ...(init.headers as Record<string, string>),
  };
  // Only set Content-Type for requests with a body (GET must not send Content-Type on some servers)
  if (method !== "GET" && method !== "HEAD") {
    headers["Content-Type"] = "application/vnd.api+json";
  }
  if (token) headers.Authorization = `Bearer ${token}`;
  else addLog(`fetchApi: no auth token for ${path}`);

  const res = await fetch(url, { ...init, headers });
  const contentType = res.headers.get("content-type") ?? "";
  const isJson =
    contentType.includes("application/json") ||
    contentType.includes("application/vnd.api+json");

  if (!res.ok) {
    const body = isJson ? await res.json().catch(() => ({})) : await res.text();
    const bodyPreview =
      typeof body === "string"
        ? body.substring(0, 400)
        : JSON.stringify(body).substring(0, 400);
    addLog(
      `API error ${res.status} ${res.statusText}: ${path} — ${bodyPreview}`
    );
    throw Object.assign(new Error(`Request failed: ${res.status}`), {
      status: res.status,
      data: body,
    });
  }

  if (isJson) return res.json() as Promise<T>;
  const text = await res.text();
  // JSON:API often sends application/vnd.api+json; if we got text, try parse
  try {
    return JSON.parse(text) as T;
  } catch {
    return text as unknown as T;
  }
}

export async function getCases(): Promise<Grievance[]> {
  const token = getToken();
  addLog(`Fetching cases from API... (token present: ${!!token})`);
  try {
    const response = await fetchApi<{ data?: unknown }>("log/activity");
    const hasData = response != null && "data" in response;
    const dataLength = Array.isArray(response?.data) ? response.data.length : hasData ? "(non-array)" : "none";
    addLog(`getCases response: data present=${hasData}, length=${String(dataLength)}`);
    if (!response?.data) {
      addLog("Invalid API response format for grievance collection");
      return [];
    }
    return mapGrievanceCollection(response);
  } catch (err: unknown) {
    const status = err && typeof err === "object" && "status" in err ? (err as { status: number }).status : "?";
    const data = err && typeof err === "object" && "data" in err ? (err as { data: unknown }).data : null;
    const msg = err instanceof Error ? err.message : String(err);
    const name = err instanceof Error ? err.name : "";
    addLog(`getCases failed: ${name} ${msg} (status=${status}, data=${data != null ? JSON.stringify(data).substring(0, 200) : "none"})`);
    throw err;
  }
}

export async function getMyCases(deviceId: string): Promise<Grievance[]> {
  const token = getToken();
  addLog(`Fetching my cases for device_id: ${deviceId}... (token present: ${!!token})`);
  try {
    const filter = `?filter[device-filter][condition][path]=field_device_id&filter[device-filter][condition][value]=${encodeURIComponent(deviceId)}`;
    const response = await fetchApi<{ data?: unknown }>(`log/activity${filter}`);
    const hasData = response != null && "data" in response;
    const dataLength = Array.isArray(response?.data) ? response.data.length : hasData ? "(non-array)" : "none";
    addLog(`getMyCases response: data present=${hasData}, length=${String(dataLength)}`);
    if (!response?.data) {
      addLog("Invalid API response format for my cases collection");
      return [];
    }
    return mapGrievanceCollection(response);
  } catch (err: unknown) {
    const status = err && typeof err === "object" && "status" in err ? (err as { status: number }).status : "?";
    const data = err && typeof err === "object" && "data" in err ? (err as { data: unknown }).data : null;
    const msg = err instanceof Error ? err.message : String(err);
    const name = err instanceof Error ? err.name : "";
    addLog(`getMyCases failed: ${name} ${msg} (status=${status}, data=${data != null ? JSON.stringify(data).substring(0, 200) : "none"})`);
    throw err;
  }
}

export async function getCaseById(id: string): Promise<Grievance> {
  const response = await fetchApi<{ data?: unknown }>(`log/activity/${id}`);
  if (!response?.data) {
    addLog("Invalid case detail response structure");
    return {} as Grievance;
  }
  return mapGrievanceInterface(response.data);
}

export async function getCaseUpdates(caseId: string): Promise<GrievanceUpdate[]> {
  const filter = `?filter[case-filter][condition][path]=entity_id.id&filter[case-filter][condition][value]=${caseId}`;
  const response = await fetchApi<{ data?: unknown }>(`comment/case_event${filter}`);
  if (!response?.data) {
    addLog("Invalid API response format for case comments");
    return [];
  }
  return mapGrievanceUpdateCollection(response);
}

export async function createCase(body: unknown): Promise<unknown> {
  const response = await fetchApi<unknown>("log/activity", {
    method: "POST",
    body: JSON.stringify(body),
  });
  return response;
}

export interface AddCaseImageParams {
  entityId: string;
  binary: Blob;
  filename: string;
  csrfToken: string;
}

export async function addCaseImage(params: AddCaseImageParams): Promise<unknown> {
  const { entityId, binary, filename, csrfToken } = params;
  const url = `${API_BASE_URL.replace(/\/$/, "")}/log/activity/${entityId}/image`;
  const token = getToken();
  
  // Ensure filename has .jpg extension if it doesn't already have an image extension
  const hasImageExt = /\.(jpg|jpeg|png|gif|webp)$/i.test(filename);
  const finalFilename = hasImageExt ? filename : `${filename}.jpg`;
  
  const headers: Record<string, string> = {
    "Content-Type": "application/octet-stream",
    "Content-Disposition": `file; filename="${finalFilename}"`,
    "X-CSRF-Token": csrfToken,
    Accept: "application/vnd.api+json",
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  addLog(`Uploading image: ${finalFilename} (size: ${binary.size} bytes)`);
  
  const res = await fetch(url, { method: "POST", body: binary, headers });
  if (!res.ok) {
    const text = await res.text();
    addLog(`addCaseImage error ${res.status}: ${text.substring(0, 200)}`);
    throw Object.assign(new Error(`Upload failed: ${res.status}`), {
      status: res.status,
      data: text,
    });
  }
  const contentType = res.headers.get("content-type");
  if (contentType?.includes("application/json")) return res.json();
  return res.text();
}

export async function getCaseImageById(ids: { id: string }[]): Promise<Image[]> {
  const results = await Promise.all(
    ids.map(({ id }) =>
      fetchApi<{
        data?: {
          id: string;
          attributes?: {
            uri?: string | { url?: string; value?: string };
          };
        };
      }>(`file/file/${id}`)
    )
  );
  return results
    .filter((r) => r?.data)
    .map((r) => {
      const uri = r!.data!.attributes?.uri;
      const path =
        typeof uri === "string"
          ? uri
          : uri?.url ?? (uri as { value?: string })?.value ?? "";
      const url = path.startsWith("http") ? path : `${API_ORIGIN}${path}`;
      return { id: r!.data!.id, url };
    });
}

export async function getOperators(): Promise<string[]> {
  const response = await fetchApi<{ data?: Array<{ attributes?: { name?: string; title?: string } }> }>("asset/structure");
  if (!response?.data || !Array.isArray(response.data)) {
    addLog("Invalid API response format for operators");
    return [];
  }
  return response.data.map(
    (item) => item.attributes?.name ?? item.attributes?.title ?? "Unknown Operator"
  );
}
